import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { inventoryApi } from '../../services/inventoryApi.js';

const empty = () => ({ supplierId: '', productId: '', targetQuantity: '', unitPrice: '', validFrom: '', validTo: '' });

export function ContractCreateModal({ open, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(empty);
  const [error, setError] = useState(null);

  const products = useQuery({ queryKey: ['inventory', 'products'], queryFn: () => inventoryApi.listProducts({ limit: 200 }).then((r) => r.items), enabled: open });
  const suppliers = useQuery({ queryKey: ['inventory', 'suppliers'], queryFn: () => inventoryApi.listSuppliers().then((r) => r.items), enabled: open });

  useEffect(() => { if (open) { setForm(empty()); setError(null); } }, [open]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = useMutation({
    mutationFn: (body) => inventoryApi.createContract(body),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['inventory', 'contracts'] }); onClose(); },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    if (!form.supplierId || !form.productId || !form.targetQuantity || Number(form.targetQuantity) <= 0) { setError(t('ct.error.required')); return; }
    save.mutate({
      supplierId: form.supplierId, productId: form.productId, targetQuantity: Number(form.targetQuantity),
      unitPrice: Number(form.unitPrice) || 0, validFrom: form.validFrom || null, validTo: form.validTo || null,
    });
  }

  return (
    <Modal open={open} onClose={onClose} title={t('ct.modal.title')}
      footer={<><Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="primary" onClick={submit} disabled={save.isPending}>{save.isPending ? `${t('common.save')}…` : t('common.save')}</Button></>}>
      <form onSubmit={submit} className="form-stack">
        <label className="field">
          <span className="field-label">{t('ct.col.supplier')}</span>
          <select className="input" value={form.supplierId} onChange={set('supplierId')} required>
            <option value="">-</option>
            {(suppliers.data || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
        <label className="field">
          <span className="field-label">{t('ct.col.product')}</span>
          <select className="input" value={form.productId} onChange={set('productId')} required>
            <option value="">-</option>
            {(products.data || []).map((p) => <option key={p.id} value={p.id}>{p.sku} · {p.name}</option>)}
          </select>
        </label>
        <div className="field-row">
          <label className="field" style={{ maxWidth: 160 }}>
            <span className="field-label">{t('ct.field.target')}</span>
            <input className="input num" type="number" min="0.0001" step="any" value={form.targetQuantity} onChange={set('targetQuantity')} required />
          </label>
          <label className="field" style={{ maxWidth: 150 }}>
            <span className="field-label">{t('ct.field.price')}</span>
            <input className="input num" type="number" min="0" step="0.01" value={form.unitPrice} onChange={set('unitPrice')} />
          </label>
        </div>
        <div className="field-row">
          <label className="field"><span className="field-label">{t('ct.field.from')}</span>
            <input className="input" type="date" value={form.validFrom} onChange={set('validFrom')} />
          </label>
          <label className="field"><span className="field-label">{t('ct.field.to')}</span>
            <input className="input" type="date" value={form.validTo} onChange={set('validTo')} />
          </label>
        </div>
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
